import { useAuthContext } from "@features/auth/contexts/AuthContext";
import {
	type RecommendationFrontendContext,
	buildRecommendationFrontendContext,
} from "@features/dashboard/recommendations/adapters/frontend-context";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { useActiveStudyPackCollectionId } from "@hooks/useActiveStudyPackCollectionId";
import type { MixedItemDist } from "@memorang/types";
import { useMemo } from "react";
import { useHasAccess } from "./useHasAccess";

const EMPTY_COLLECTION_IDS: number[] = [];

export const useRecommendationFrontendContext = ({
	studyPackData,
	additionalExamCollectionIds = EMPTY_COLLECTION_IDS,
}: {
	studyPackData?: MixedItemDist;
	additionalExamCollectionIds?: number[];
}): RecommendationFrontendContext => {
	const { hasStudyPackContentAccess } = useHasAccess();
	const { userInfo, userPublicMetadataId } = useAuthContext();
	const {
		currentExamContext: {
			examId,
			studyPackCollectionId,
			freeStudyPackCollectionId,
		},
	} = useExamContext();
	const { collectionId: activeStudyPackCollectionId } =
		useActiveStudyPackCollectionId();

	return useMemo(
		() =>
			buildRecommendationFrontendContext({
				userId: userPublicMetadataId,
				examId,
				activeStudyPackCollectionId: activeStudyPackCollectionId ?? undefined,
				studyPackCollectionId,
				freeStudyPackCollectionId,
				examCollectionIds: [
					...(studyPackData?.exams ?? []).map((exam) => exam.collectionId),
					...additionalExamCollectionIds,
				],
				tags: studyPackData?.tags ?? [],
				freeTagIds: studyPackData?.freeTagIds ?? [],
				hasPremiumAccess: hasStudyPackContentAccess,
				targetLevelId: userInfo?.onboarding?.profile?.target_level_id,
				onboardingSkillIds: userInfo?.onboarding?.profile?.skill_ids,
			}),
		[
			userPublicMetadataId,
			examId,
			activeStudyPackCollectionId,
			studyPackCollectionId,
			freeStudyPackCollectionId,
			studyPackData,
			additionalExamCollectionIds,
			hasStudyPackContentAccess,
			userInfo?.onboarding?.profile?.target_level_id,
			userInfo?.onboarding?.profile?.skill_ids,
		],
	);
};
